
import React, { useState } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Avatar from '../common/Avatar';
import Notifications from '../common/Notifications';
import { useTheme } from '../../contexts/ThemeContext';
import { useCart } from '../../contexts/CartContext';
import CartDropdown from '../common/CartDropdown';
import { useTranslation } from 'react-i18next';

const { Link } = ReactRouterDOM as any;

const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const SunIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M12 12a5 5 0 100-10 5 5 0 000 10z" />
  </svg>
);
const MoonIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);
const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const CogIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0 3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);
const CartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

interface ClientNavbarProps {
  toggleSidebar: () => void;
}

const ClientNavbar: React.FC<ClientNavbarProps> = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCartDropdownOpen, setIsCartDropdownOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const current = i18n.language;
    const next = current.startsWith('fr') ? 'en' : current.startsWith('en') ? 'ar' : 'fr';
    i18n.changeLanguage(next);
  };

  const getFlagUrl = (lang: string) => {
    if (lang.startsWith('ar')) return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/tn.png';
    if (lang.startsWith('en')) return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/gb.png';
    return 'https://cisqrqvwdettctssylrl.supabase.co/storage/v1/object/public/public_pic/branding/fr.png';
  };

  const userMenuPanelClass =
    'absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl border border-border bg-surface-card py-1 shadow-premium ring-1 ring-primary-500/10 rtl:left-0 rtl:right-auto rtl:origin-top-left dark:ring-white/5';

  return (
    <header className="dashboard-topbar px-4 sm:px-6 lg:px-8">
      <div className="dashboard-topbar-brand-line" aria-hidden />

      <div className="flex items-center">
        <button
          type="button"
          onClick={toggleSidebar}
          className="dashboard-topbar-icon-btn lg:hidden ltr:mr-3 rtl:ml-3 rtl:mr-0"
          aria-label="Open sidebar"
        >
          <MenuIcon className="h-6 w-6" />
        </button>

        <div className="relative hidden md:block">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3">
            <SearchIcon className="h-5 w-5 text-foreground-muted" />
          </span>
          <input type="text" placeholder={t('common.search')} className="dashboard-topbar-search" />
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 rtl:flex-row-reverse">
        <div className="dashboard-topbar-actions">
          <button
            type="button"
            onClick={toggleLanguage}
            className="dashboard-topbar-icon-btn overflow-hidden rounded-lg p-1 ring-1 ring-border/80 transition-shadow hover:ring-primary-500/35 dark:ring-white/15"
            title={t('common.language')}
          >
            <img src={getFlagUrl(i18n.language)} alt="" className="h-6 w-9 rounded object-cover shadow-sm" />
          </button>

          <button type="button" onClick={toggleTheme} className="dashboard-topbar-icon-btn" aria-label={isDarkMode ? 'Light mode' : 'Dark mode'}>
            {isDarkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsCartDropdownOpen(!isCartDropdownOpen)}
              className="dashboard-topbar-icon-btn relative"
              aria-label={t('nav.cart', { defaultValue: 'Panier' })}
            >
              <CartIcon className="h-6 w-6" />
              {cartItems.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-surface-card bg-gradient-to-br from-primary-500 to-primary-600 px-1 text-[10px] font-bold text-white shadow-sm dark:border-slate-900">
                  {cartItems.length > 99 ? '99+' : cartItems.length}
                </span>
              )}
            </button>
            <CartDropdown isOpen={isCartDropdownOpen} onClose={() => setIsCartDropdownOpen(false)} />
          </div>

          <Notifications triggerClassName="dashboard-topbar-icon-btn" />
        </div>

        <div className="hidden h-8 w-px bg-border sm:block dark:bg-white/10" aria-hidden />

        <div className="relative">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="ui-focus-visible flex max-w-xs items-center rounded-full ring-2 ring-transparent transition-all hover:ring-primary-500/30 dark:hover:ring-primary-400/25"
            id="user-menu-button"
            aria-expanded={isUserMenuOpen}
            aria-haspopup="true"
          >
            <span className="sr-only">Open user menu</span>
            <Avatar user={user} />
          </button>
          {isUserMenuOpen && (
            <div
              className={userMenuPanelClass}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="user-menu-button"
              onMouseLeave={() => setIsUserMenuOpen(false)}
            >
              <div className="h-0.5 bg-gradient-brand rounded-t-[inherit]" aria-hidden />
              <div className="border-b border-border px-4 py-3 text-sm">
                <p className="font-semibold text-foreground">
                  {user?.prénom} {user?.nom}
                </p>
                <p className="truncate text-xs text-foreground-muted">{user?.email}</p>
              </div>

              <Link
                to="/dashboard/profile"
                className="flex items-center px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-primary-500/10"
                role="menuitem"
                onClick={() => setIsUserMenuOpen(false)}
              >
                <UserIcon className="h-4 w-4 ltr:mr-2 rtl:ml-2 rtl:mr-0" />
                {t('common.profile')}
              </Link>
              <Link
                to="/dashboard/settings"
                className="flex items-center px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-primary-500/10"
                role="menuitem"
                onClick={() => setIsUserMenuOpen(false)}
              >
                <CogIcon className="h-4 w-4 ltr:mr-2 rtl:ml-2 rtl:mr-0" />
                {t('common.settings')}
              </Link>

              <div className="my-1 border-t border-border" />

              <button
                type="button"
                onClick={() => {
                  logout();
                  setIsUserMenuOpen(false);
                }}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-500/10 rtl:text-right dark:text-red-400"
                role="menuitem"
              >
                <LogoutIcon className="h-4 w-4 ltr:mr-2 rtl:ml-2 rtl:mr-0" />
                {t('common.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ClientNavbar;
